import { registrationSlug } from '@chukua/common';
import { fleetTabPath } from '../../fleet.paths';

/**
 * A unit's own page: a truck and the trailer it pulls, at `/fleet/units/<registration>/<tab>` (gitaumoses4/chukua#390). Each tab
 * shows both vehicles where both have something; service and parts are the truck's alone, as trailers have no service plan or
 * work orders yet.
 */
export const UNIT_TABS = [
  'details',
  'tyres',
  'documents',
  'service-plan',
  'service-records',
  'schedule',
  'parts',
  'part-life',
  'ownership',
  'couplings',
  'history',
] as const;
export type UnitTab = (typeof UNIT_TABS)[number];

export const UNIT_TAB_LABELS: Record<UnitTab, string> = {
  details: 'Details',
  tyres: 'Tyres',
  documents: 'Documents',
  'service-plan': 'Service plan',
  'service-records': 'Service records',
  schedule: 'Schedule',
  parts: 'Parts',
  'part-life': 'Part life',
  ownership: 'Ownership',
  couplings: 'Trailer history',
  history: 'History',
};

/** A spare trailer, on no truck, keeps a page of its own at the same kind of address, with the tabs a trailer has. */
export const SPARE_TRAILER_TABS = ['details', 'tyres', 'documents', 'ownership', 'couplings', 'history'] as const satisfies readonly UnitTab[];
export type SpareTrailerTab = (typeof SPARE_TRAILER_TABS)[number];

export const SPARE_TRAILER_TAB_LABELS: Record<SpareTrailerTab, string> = { ...UNIT_TAB_LABELS, couplings: 'Truck history' };

export function isSpareTrailerTab(tab: UnitTab): tab is SpareTrailerTab {
  return (SPARE_TRAILER_TABS as readonly UnitTab[]).includes(tab);
}

/** The tabs that need `maintenance.view`. */
export function isMaintenanceTab(tab: UnitTab): boolean {
  return tab === 'tyres' || tab === 'service-plan' || tab === 'service-records' || tab === 'schedule' || tab === 'parts' || tab === 'part-life';
}

/** The two vehicles a unit is made of. */
export type UnitVehicle = 'truck' | 'trailer';

/** A truck's or a trailer's registration, as the address of its unit: a coupled trailer's address opens its truck's unit. */
export function unitPath(registration: string, tab: UnitTab = 'details'): string {
  return `${fleetTabPath('units')}/${encodeURIComponent(registrationSlug(registration))}/${tab}`;
}
